import React, { useState } from 'react';
import { QuickFitPlayerForm, QuickFitClubOptions } from '../services/quickFitService';

const FLEX_OPTIONS = ['L', 'A', 'R', 'S', 'X'];
const WEDGE_LOFT_OPTIONS = [48, 50, 52, 54, 56, 58, 60];
const TEMPO_OPTIONS = [
  { value: 'smooth', label: 'Smooth' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'aggressive', label: 'Aggressive' },
];
const IRON_TYPE_OPTIONS = [
  'Blade',
  'Players',
  'Players Distance',
  'Game Improvement',
  'Super Game Improvement',
];

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  player: QuickFitPlayerForm;
  index: number;
  clubOptions: QuickFitClubOptions;
  onChange: (updated: QuickFitPlayerForm) => void;
  onRemove: () => void;
  canRemove: boolean;
}

// ── Component ─────────────────────────────────────────────────────────────────

const QuickFitPlayerCard: React.FC<Props> = ({ player, index, clubOptions, onChange, onRemove, canRemove }) => {
  const [expanded, setExpanded] = useState(true);

  // Filtered driver models for selected brand
  const driverModels = player.driver_brand
    ? clubOptions.drivers
        .filter(d => d.brand === player.driver_brand)
        .map(d => d.model)
        .filter((v, i, a) => a.indexOf(v) === i)
    : [];

  // Lofts for selected driver brand+model
  const driverLofts = player.driver_brand && player.driver_model
    ? clubOptions.drivers
        .filter(d => d.brand === player.driver_brand && d.model === player.driver_model)
        .flatMap(d => d.lofts)
    : [];

  // Iron brands
  const ironBrands = Array.from(new Set(clubOptions.irons.map(i => i.brand))).sort();

  // Iron models for selected brand
  const ironModels = player.iron_brand
    ? clubOptions.irons.filter(i => i.brand === player.iron_brand).map(i => i.model)
    : [];

  // Driver brands
  const driverBrands = Array.from(new Set(clubOptions.drivers.map(d => d.brand))).sort();

  function set(field: keyof QuickFitPlayerForm, value: any) {
    onChange({ ...player, [field]: value });
  }

  function handleDriverBrandChange(brand: string) {
    onChange({ ...player, driver_brand: brand, driver_model: '', driver_loft: '' });
  }

  function handleDriverModelChange(model: string) {
    onChange({ ...player, driver_model: model, driver_loft: '' });
  }

  function handleIronBrandChange(brand: string) {
    onChange({ ...player, iron_brand: brand, iron_model: '' });
  }

  function toggleWedgeLoft(loft: number) {
    const current = player.wedge_lofts;
    const next = current.includes(loft)
      ? current.filter(l => l !== loft)
      : [...current, loft].sort((a, b) => a - b);
    set('wedge_lofts', next);
  }

  const inputCls = 'w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-green-ink';
  const labelCls = 'block text-xs font-medium text-gray-600 mb-1';
  const selectCls = `${inputCls} bg-white`; // used for club dropdowns below

  const driverArchetypeFields = [
    player.smash_factor_driver,
    player.face_angle_std_dev_driver,
    player.spin_rate_std_dev_driver,
    player.carry_std_dev_driver,
    player.dispersion_std_dev_driver,
  ].filter(Boolean).length;
  const ironArchetypeFields = [
    player.smash_factor_7i,
    player.face_angle_std_dev_7i,
    player.spin_rate_std_dev_7i,
    player.carry_std_dev_7i,
    player.dispersion_std_dev_7i || player.dispersion_std_dev_driver,
  ].filter(Boolean).length;

  return (
    <div className="border border-gray-200 rounded-lg mb-4 overflow-hidden">
      {/* Card header */}
      <div
        className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer select-none"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-gray-700">
            {player.name || `Player ${index + 1}`}
          </span>
          {player.handicap && (
            <span className="text-xs text-gray-500">HCP {player.handicap}</span>
          )}
          {player.swing_speed_driver && (
            <span className="text-xs text-gray-500">{player.swing_speed_driver} mph</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canRemove && (
            <button
              onClick={e => { e.stopPropagation(); onRemove(); }}
              className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50"
            >
              Remove
            </button>
          )}
          <span className="text-gray-400 text-sm">{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {expanded && (
        <div className="p-4 space-y-5">
          {/* ── Section 1: Player Info ───────────────────────────────── */}
          <div>
            <h4 className="text-xs font-semibold text-green-ink uppercase tracking-wide mb-3">
              Player Info
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {/* Name */}
              <div className="col-span-2 sm:col-span-1">
                <label className={labelCls}>Name</label>
                <input
                  type="text"
                  className={inputCls}
                  value={player.name}
                  onChange={e => set('name', e.target.value)}
                  placeholder="e.g. Dave"
                />
              </div>

              {/* Handicap */}
              <div>
                <label className={labelCls}>Handicap (0–54)</label>
                <input
                  type="number"
                  className={inputCls}
                  value={player.handicap}
                  onChange={e => set('handicap', e.target.value)}
                  min={0}
                  max={40}
                  placeholder="15"
                />
              </div>

              {/* Age */}
              <div>
                <label className={labelCls}>Age</label>
                <input
                  type="number"
                  className={inputCls}
                  value={player.age}
                  onChange={e => set('age', e.target.value)}
                  min={12}
                  max={90}
                  placeholder="45"
                />
              </div>

              {/* Gender */}
              <div>
                <label className={labelCls}>Gender</label>
                <div className="flex gap-3 mt-2">
                  {(['Male', 'Female'] as const).map(g => (
                    <label key={g} className="flex items-center gap-1 text-sm cursor-pointer">
                      <input
                        type="radio"
                        name={`gender-${player.id}`}
                        value={g}
                        checked={player.gender === g}
                        onChange={() => set('gender', g)}
                        className="accent-green-ink"
                      />
                      {g}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className={labelCls}>Tempo <span className="font-normal text-gray-400">opt</span></label>
                <select
                  className={selectCls}
                  value={player.swing_tempo}
                  onChange={e => set('swing_tempo', e.target.value)}
                >
                  <option value="">Unknown</option>
                  {TEMPO_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* ── Section 2: Swing Data ────────────────────────────────── */}
          <div>
            <h4 className="text-xs font-semibold text-green-ink uppercase tracking-wide mb-3">
              Swing Data
            </h4>

            {/* Row 1: Core speed + attack angle */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
              <div>
                <label className={labelCls}>Driver swing speed (mph)</label>
                <input type="number" className={inputCls}
                  value={player.swing_speed_driver}
                  onChange={e => set('swing_speed_driver', e.target.value)}
                  min={60} max={125} placeholder="93" />
                <p className="text-xs text-gray-400 mt-0.5">Avg amateur = 93 mph</p>
              </div>

              <div>
                <label className={labelCls}>7-iron swing speed (mph)</label>
                <input type="number" className={inputCls}
                  value={player.swing_speed_7iron}
                  onChange={e => set('swing_speed_7iron', e.target.value)}
                  min={45} max={100} placeholder="78" />
                <p className="text-xs text-gray-400 mt-0.5">~60–65% of driver speed</p>
              </div>

              <div>
                <label className={labelCls}>Driver attack angle (°)</label>
                <input type="number" className={inputCls}
                  value={player.attack_angle_driver === 0 && player.swing_speed_driver === '' ? '' : player.attack_angle_driver}
                  onChange={e => set('attack_angle_driver', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                  min={-10} max={8} step={0.5} placeholder="0" />
                <p className="text-xs text-gray-400 mt-0.5">+3 = hit up · 0 = level · -4 = steep</p>
              </div>

              <div>
                <label className={labelCls}>Iron attack angle (°)</label>
                <input type="number" className={inputCls}
                  value={player.attack_angle_irons}
                  onChange={e => set('attack_angle_irons', e.target.value === '' ? -3 : parseFloat(e.target.value))}
                  min={-10} max={2} step={0.5} placeholder="-3" />
                <p className="text-xs text-gray-400 mt-0.5">-1 = shallow · -3 = normal · -7 = steep</p>
              </div>
            </div>

            {/* Row 2: Launch angle + spin rates + dispersion */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-3">
              <div>
                <label className={labelCls}>Driver launch angle (°) <span className="font-normal text-gray-400">opt</span></label>
                <input type="number" className={inputCls}
                  value={player.launch_angle_driver}
                  onChange={e => set('launch_angle_driver', e.target.value)}
                  min={6} max={20} step={0.5} placeholder="13" />
                <p className="text-xs text-gray-400 mt-0.5">Adjusts optimal spin target</p>
              </div>

              <div>
                <label className={labelCls}>7-iron launch angle (°) <span className="font-normal text-gray-400">opt</span></label>
                <input type="number" className={inputCls}
                  value={player.launch_angle_7i}
                  onChange={e => set('launch_angle_7i', e.target.value)}
                  min={5} max={30} step={0.5} placeholder="18" />
                <p className="text-xs text-gray-400 mt-0.5">Used directly by iron V9 launch diagnosis</p>
              </div>

              <div>
                <label className={labelCls}>Driver spin rate (RPM) <span className="font-normal text-gray-400">opt</span></label>
                <input type="number" className={inputCls}
                  value={player.spin_rate_driver}
                  onChange={e => set('spin_rate_driver', e.target.value)}
                  min={1500} max={5000} placeholder="2500" />
                <p className="text-xs text-gray-400 mt-0.5">Typical: 2000–3500 rpm</p>
              </div>

              <div>
                <label className={labelCls}>7-iron spin rate (RPM) <span className="font-normal text-gray-400">opt</span></label>
                <input type="number" className={inputCls}
                  value={player.spin_rate_7i}
                  onChange={e => set('spin_rate_7i', e.target.value)}
                  min={3000} max={12000} placeholder="7000" />
                <p className="text-xs text-gray-400 mt-0.5">Typical: 6000–8000 rpm</p>
              </div>

              <div>
                <label className={labelCls}>Driver carry (yds) <span className="font-normal text-gray-400">opt</span></label>
                <input type="number" className={inputCls}
                  value={player.driver_carry_distance}
                  onChange={e => set('driver_carry_distance', e.target.value)}
                  min={50} max={400} placeholder="245" />
                <p className="text-xs text-gray-400 mt-0.5">Distance need vs speed benchmark</p>
              </div>

              <div>
                <label className={labelCls}>7-iron carry (yds) <span className="font-normal text-gray-400">opt</span></label>
                <input type="number" className={inputCls}
                  value={player.carry_distance_7i}
                  onChange={e => set('carry_distance_7i', e.target.value)}
                  min={50} max={220} placeholder="155" />
                <p className="text-xs text-gray-400 mt-0.5">Iron distance need vs speed benchmark</p>
              </div>

              <div>
                <label className={labelCls}>Driver dispersion (yds) <span className="font-normal text-gray-400">opt</span></label>
                <input type="number" className={inputCls}
                  value={player.dispersion_std_dev_driver}
                  onChange={e => set('dispersion_std_dev_driver', e.target.value)}
                  min={5} max={40} placeholder="25" />
                <p className="text-xs text-gray-400 mt-0.5">L/R std dev — typical: 15–40 yds</p>
              </div>

              <div>
                <label className={labelCls}>7-iron dispersion (yds) <span className="font-normal text-gray-400">opt</span></label>
                <input type="number" className={inputCls}
                  value={player.dispersion_std_dev_7i}
                  onChange={e => set('dispersion_std_dev_7i', e.target.value)}
                  min={5} max={30} placeholder="15" />
                <p className="text-xs text-gray-400 mt-0.5">Iron forgiveness and archetype input</p>
              </div>

              <div>
                <label className={labelCls}>Miss direction <span className="font-normal text-gray-400">opt</span></label>
                <div className="flex gap-1 mt-1 flex-wrap">
                  {(['', 'fade', 'straight', 'draw'] as const).map(p => (
                    <button key={p} type="button"
                      onClick={() => set('dispersion_pattern_driver', p)}
                      className={`px-2 py-1 text-xs rounded border ${
                        player.dispersion_pattern_driver === p
                          ? 'bg-green-ink text-white border-green-ink'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-green-ink'
                      }`}>
                      {p === '' ? '?' : p.charAt(0).toUpperCase() + p.slice(1)}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-1">For right-handed golfer</p>
              </div>
            </div>

            {/* Row 3: V9 quality and consistency data */}
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 mb-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-3">
                <div>
                  <h5 className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">
                    V9 Physics + AI Test Data
                  </h5>
                  <p className="text-xs text-emerald-700">
                    Archetype LM fields: driver {driverArchetypeFields}/5, iron {ironArchetypeFields}/5.
                    V9 uses non-fallback classification at 3+ fields.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                <div>
                  <label className={labelCls}>Driver smash <span className="font-normal text-gray-400">opt</span></label>
                  <input type="number" className={inputCls}
                    value={player.smash_factor_driver}
                    onChange={e => set('smash_factor_driver', e.target.value)}
                    min={1.4} max={1.52} step={0.01} placeholder="1.47" />
                  <p className="text-xs text-gray-400 mt-0.5">Blank = handicap fallback</p>
                </div>

                <div>
                  <label className={labelCls}>7-iron smash <span className="font-normal text-gray-400">opt</span></label>
                  <input type="number" className={inputCls}
                    value={player.smash_factor_7i}
                    onChange={e => set('smash_factor_7i', e.target.value)}
                    min={1.33} max={1.42} step={0.01} placeholder="1.36" />
                  <p className="text-xs text-gray-400 mt-0.5">Iron strike-quality input</p>
                </div>

                <div>
                  <label className={labelCls}>Driver face std dev (°) <span className="font-normal text-gray-400">opt</span></label>
                  <input type="number" className={inputCls}
                    value={player.face_angle_std_dev_driver}
                    onChange={e => set('face_angle_std_dev_driver', e.target.value)}
                    min={0.1} max={15} step={0.1} placeholder="1.5" />
                </div>

                <div>
                  <label className={labelCls}>7i face std dev (°) <span className="font-normal text-gray-400">opt</span></label>
                  <input type="number" className={inputCls}
                    value={player.face_angle_std_dev_7i}
                    onChange={e => set('face_angle_std_dev_7i', e.target.value)}
                    min={0.1} max={15} step={0.1} placeholder="1.2" />
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                <div>
                  <label className={labelCls}>Driver spin std dev (RPM) <span className="font-normal text-gray-400">opt</span></label>
                  <input type="number" className={inputCls}
                    value={player.spin_rate_std_dev_driver}
                    onChange={e => set('spin_rate_std_dev_driver', e.target.value)}
                    min={100} max={800} placeholder="250" />
                </div>

                <div>
                  <label className={labelCls}>7i spin std dev (RPM) <span className="font-normal text-gray-400">opt</span></label>
                  <input type="number" className={inputCls}
                    value={player.spin_rate_std_dev_7i}
                    onChange={e => set('spin_rate_std_dev_7i', e.target.value)}
                    min={100} max={1500} placeholder="200" />
                </div>

                <div>
                  <label className={labelCls}>Driver launch std dev (°) <span className="font-normal text-gray-400">opt</span></label>
                  <input type="number" className={inputCls}
                    value={player.launch_angle_std_dev_driver}
                    onChange={e => set('launch_angle_std_dev_driver', e.target.value)}
                    min={0.5} max={5} step={0.1} placeholder="1.5" />
                </div>

                <div>
                  <label className={labelCls}>7i launch std dev (°) <span className="font-normal text-gray-400">opt</span></label>
                  <input type="number" className={inputCls}
                    value={player.launch_angle_std_dev_7i}
                    onChange={e => set('launch_angle_std_dev_7i', e.target.value)}
                    min={0.5} max={5} step={0.1} placeholder="1.2" />
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className={labelCls}>Driver carry std dev (yds) <span className="font-normal text-gray-400">opt</span></label>
                <input type="number" className={inputCls}
                  value={player.carry_std_dev_driver}
                  onChange={e => set('carry_std_dev_driver', e.target.value)}
                  min={3} max={25} placeholder="Leave blank if unknown" />
                <p className="text-xs text-gray-400 mt-0.5">Shot-to-shot carry variation</p>
              </div>

              <div>
                <label className={labelCls}>7-iron carry std dev (yds) <span className="font-normal text-gray-400">opt</span></label>
                <input type="number" className={inputCls}
                  value={player.carry_std_dev_7i}
                  onChange={e => set('carry_std_dev_7i', e.target.value)}
                  min={3} max={25} placeholder="Leave blank if unknown" />
                <p className="text-xs text-gray-400 mt-0.5">Shot-to-shot carry variation</p>
              </div>

                <div>
                  <label className={labelCls}>7i ball speed std dev (mph) <span className="font-normal text-gray-400">opt</span></label>
                  <input type="number" className={inputCls}
                    value={player.ball_speed_std_dev_7i}
                    onChange={e => set('ball_speed_std_dev_7i', e.target.value)}
                    min={1} max={8} step={0.1} placeholder="Leave blank if unknown" />
                  <p className="text-xs text-gray-400 mt-0.5">Iron strike consistency</p>
                </div>

              <div>
                <label className={labelCls}>Driver ball speed std dev (mph) <span className="font-normal text-gray-400">opt</span></label>
                <input type="number" className={inputCls}
                  value={player.ball_speed_std_dev_driver}
                  onChange={e => set('ball_speed_std_dev_driver', e.target.value)}
                  min={1} max={8} placeholder="Leave blank if unknown" />
                <p className="text-xs text-gray-400 mt-0.5">Ball speed consistency</p>
              </div>

                <div>
                  <label className={labelCls}>Driver path std dev (°) <span className="font-normal text-gray-400">opt</span></label>
                  <input type="number" className={inputCls}
                    value={player.club_path_std_dev_driver}
                    onChange={e => set('club_path_std_dev_driver', e.target.value)}
                    min={0.1} max={15} step={0.1} placeholder="2.0" />
                </div>

                <div>
                  <label className={labelCls}>7i path std dev (°) <span className="font-normal text-gray-400">opt</span></label>
                  <input type="number" className={inputCls}
                    value={player.club_path_std_dev_7i}
                    onChange={e => set('club_path_std_dev_7i', e.target.value)}
                    min={0.1} max={15} step={0.1} placeholder="1.8" />
                </div>

                <div>
                  <label className={labelCls}>Driver face-to-path (°) <span className="font-normal text-gray-400">opt</span></label>
                  <input type="number" className={inputCls}
                    value={player.face_to_path_driver}
                    onChange={e => set('face_to_path_driver', e.target.value)}
                    min={-10} max={10} step={0.1} placeholder="0" />
                  <p className="text-xs text-gray-400 mt-0.5">Shaft stability input</p>
                </div>
              </div>
            </div>
          </div>

          {/* ── Section 3: Current Clubs ─────────────────────────────── */}
          <div>
            <h4 className="text-xs font-semibold text-green-ink uppercase tracking-wide mb-1">
              Current Clubs
            </h4>
            <p className="text-xs text-gray-400 mb-3">Optional — improves accuracy. Select "Don't know" to skip any field.</p>

            {/* Driver row */}
            <div className="mb-4">
              <p className="text-xs font-medium text-gray-500 mb-2">Driver</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <label className={labelCls}>Brand</label>
                  <select
                    className={selectCls}
                    value={player.driver_brand}
                    onChange={e => handleDriverBrandChange(e.target.value)}
                  >
                    <option value="">Don't know</option>
                    {driverBrands.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Model</label>
                  <select
                    className={selectCls}
                    value={player.driver_model}
                    onChange={e => handleDriverModelChange(e.target.value)}
                    disabled={!player.driver_brand}
                  >
                    <option value="">Don't know</option>
                    {driverModels.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Loft</label>
                  <select
                    className={selectCls}
                    value={player.driver_loft}
                    onChange={e => set('driver_loft', e.target.value)}
                    disabled={!player.driver_model}
                  >
                    <option value="">Don't know</option>
                    {driverLofts.map(l => <option key={l} value={l}>{l}°</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Actual static loft <span className="font-normal text-gray-400">opt</span></label>
                  <input
                    type="number"
                    className={inputCls}
                    value={player.driver_static_loft}
                    onChange={e => set('driver_static_loft', e.target.value)}
                    min={5}
                    max={16}
                    step={0.5}
                    placeholder={player.driver_loft || '10.5'}
                  />
                </div>
                <div>
                  <label className={labelCls}>Shaft flex</label>
                  <div className="flex gap-1 mt-1 flex-wrap">
                    <button
                      type="button"
                      onClick={() => set('driver_shaft_flex', '')}
                      className={`px-2 py-1 text-xs rounded border ${
                        player.driver_shaft_flex === ''
                          ? 'bg-green-ink text-white border-green-ink'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-green-ink'
                      }`}
                    >
                      ?
                    </button>
                    {FLEX_OPTIONS.map(f => (
                      <button
                        key={f}
                        type="button"
                        onClick={() => set('driver_shaft_flex', f)}
                        className={`px-2 py-1 text-xs rounded border ${
                          player.driver_shaft_flex === f
                            ? 'bg-green-ink text-white border-green-ink'
                            : 'bg-white text-gray-600 border-gray-300 hover:border-green-ink'
                        }`}
                      >
                        {f}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Shaft model <span className="font-normal text-gray-400">opt</span></label>
                  <input
                    type="text"
                    className={inputCls}
                    value={player.driver_shaft_model}
                    onChange={e => set('driver_shaft_model', e.target.value)}
                    placeholder="e.g. Ventus Blue"
                  />
                </div>
                <div>
                  <label className={labelCls}>Shaft weight (g) <span className="font-normal text-gray-400">opt</span></label>
                  <input
                    type="number"
                    className={inputCls}
                    value={player.driver_shaft_weight}
                    onChange={e => set('driver_shaft_weight', e.target.value)}
                    min={30}
                    max={120}
                    placeholder="60"
                  />
                </div>
              </div>
            </div>

            {/* Iron row */}
            <div className="mb-4">
              <p className="text-xs font-medium text-gray-500 mb-2">Irons</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <label className={labelCls}>Brand</label>
                  <select
                    className={selectCls}
                    value={player.iron_brand}
                    onChange={e => handleIronBrandChange(e.target.value)}
                  >
                    <option value="">Don't know</option>
                    {ironBrands.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Model</label>
                  <select
                    className={selectCls}
                    value={player.iron_model}
                    onChange={e => set('iron_model', e.target.value)}
                    disabled={!player.iron_brand}
                  >
                    <option value="">Don't know</option>
                    {ironModels.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Iron type <span className="font-normal text-gray-400">opt</span></label>
                  <select
                    className={selectCls}
                    value={player.iron_type}
                    onChange={e => set('iron_type', e.target.value)}
                  >
                    <option value="">Unknown</option>
                    {IRON_TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Shaft flex</label>
                  <div className="flex gap-1 mt-1 flex-wrap">
                    <button
                      type="button"
                      onClick={() => set('iron_shaft_flex', '')}
                      className={`px-2 py-1 text-xs rounded border ${
                        player.iron_shaft_flex === ''
                          ? 'bg-green-ink text-white border-green-ink'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-green-ink'
                      }`}
                    >
                      ?
                    </button>
                    {FLEX_OPTIONS.map(f => (
                      <button
                        key={f}
                        type="button"
                        onClick={() => set('iron_shaft_flex', f)}
                        className={`px-2 py-1 text-xs rounded border ${
                          player.iron_shaft_flex === f
                            ? 'bg-green-ink text-white border-green-ink'
                            : 'bg-white text-gray-600 border-gray-300 hover:border-green-ink'
                        }`}
                      >
                        {f}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Shaft model <span className="font-normal text-gray-400">opt</span></label>
                  <input
                    type="text"
                    className={inputCls}
                    value={player.iron_shaft_model}
                    onChange={e => set('iron_shaft_model', e.target.value)}
                    placeholder="e.g. Dynamic Gold"
                  />
                </div>
                <div>
                  <label className={labelCls}>Shaft weight (g) <span className="font-normal text-gray-400">opt</span></label>
                  <input
                    type="number"
                    className={inputCls}
                    value={player.iron_shaft_weight}
                    onChange={e => set('iron_shaft_weight', e.target.value)}
                    min={40}
                    max={160}
                    placeholder="105"
                  />
                </div>
              </div>
            </div>

            {/* Wedge lofts */}
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2">Wedge lofts carried</p>
              <div className="flex gap-2 flex-wrap">
                {WEDGE_LOFT_OPTIONS.map(loft => (
                  <button
                    key={loft}
                    type="button"
                    onClick={() => toggleWedgeLoft(loft)}
                    className={`px-3 py-1.5 text-xs rounded border font-medium ${
                      player.wedge_lofts.includes(loft)
                        ? 'bg-green-ink text-white border-green-ink'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-green-ink'
                    }`}
                  >
                    {loft}°
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickFitPlayerCard;
